import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User, Trash2, Check, X, MessageCircle, Undo2 } from 'lucide-react';
import type { AudioComment } from '../services/audioCommentService';
import type { UserSearchResult } from '../services/userService';
import { audioCommentsPagePath } from '../utils/appRoutes';

interface AudioCommentTileProps {
  comment: AudioComment;
  trackId: string;
  fileId: string;
  onDelete: (threadId: string) => void;
  onResolve: (threadId: string, resolved: boolean) => void;
  canComment: boolean;
  currentUserId?: string;
  onClose: () => void;
  userMap?: Record<string, UserSearchResult>;
}

const AudioCommentTile: React.FC<AudioCommentTileProps> = ({
  comment,
  trackId,
  fileId,
  onDelete,
  onResolve,
  canComment,
  currentUserId,
  onClose,
  userMap = {},
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isAuthor = currentUserId === comment.authorUserId;
  // In read-only mode, authors should NOT see edit controls.
  // Only users with canComment (editor permission) can edit/resolve/delete.
  const canEdit = !!canComment;

  const handleViewThread = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(audioCommentsPagePath(trackId, fileId, { selectedComment: comment.threadId }));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t('audioComments.confirmDeleteComment'))) {
      onDelete(comment.threadId);
      onClose();
    }
  };

  const handleResolve = (e: React.MouseEvent) => {
    e.stopPropagation();
    onResolve(comment.threadId, !comment.resolved);
    onClose();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const formatTimestamp = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getUserDisplay = () => {
    if (isAuthor) {
      return t('audioComments.you');
    }
    const user = userMap[comment.authorUserId];
    return user ? user.email : comment.authorUserId;
  };

  return (
    <div 
      className="w-full rounded-lg border p-4 shadow-sm"
      style={{ 
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold" style={{ color: 'var(--brand-from)' }}>
            @{formatTimestamp(comment.timestampMs)}
          </span>
          {comment.resolved && (
            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--info-green-bg)', color: 'var(--info-green-text)' }}>
              {t('audioComments.resolved')}
            </span>
          )}
        </div>
        <button
          onClick={handleClose}
          className="w-6 h-6 flex items-center justify-center rounded-full transition-all duration-200 hover:shadow-sm"
          style={{ 
            color: 'var(--color-muted)',
            backgroundColor: 'var(--color-hover)'
          }}
          title={t('buttons.close')}
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Comment text */}
      <p className="text-sm mb-3" style={{ color: 'var(--color-text)' }}>
        {comment.text}
      </p>

      {/* Author info */}
      <div className="flex items-center gap-2 text-xs mb-3" style={{ color: 'var(--color-muted)' }}>
        <User className="w-3 h-3" style={{ color: 'var(--info-blue-border)' }} />
        <span>{getUserDisplay()}</span>
      </div>

      {/* Reply count */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="flex items-center gap-1 text-xs mb-3" style={{ color: 'var(--color-muted)' }}>
          <MessageCircle className="w-3 h-3" />
          <span>
            {comment.replies.length} {comment.replies.length === 1 ? t('audioComments.reply') : t('audioComments.replies')}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleViewThread}
          className="flex-1 px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 hover:shadow-sm flex items-center justify-center gap-1"
          style={{ 
            backgroundColor: 'var(--brand-from)', 
            color: 'white',
            border: '1px solid var(--brand-from)'
          }}
        >
          {t('audioComments.viewThread')}
        </button>

        {canEdit && (
          <>
            <button
              onClick={handleResolve}
              className="px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 hover:shadow-sm flex items-center justify-center"
              style={{ 
                backgroundColor: comment.resolved ? 'var(--color-hover)' : 'var(--info-green-bg)',
                color: comment.resolved ? 'var(--color-text)' : 'var(--info-green-text)',
                border: `1px solid ${comment.resolved ? 'var(--color-border)' : 'var(--info-green-border)'}`
              }}
              title={comment.resolved ? t('audioComments.unresolve') : t('audioComments.resolve')}
            >
              {comment.resolved ? (
                <Undo2 className="w-3 h-3" />
              ) : (
                <Check className="w-3 h-3" />
              )}
            </button>

            <button
              onClick={handleDelete}
              className="px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 hover:shadow-sm flex items-center justify-center"
              style={{ 
                backgroundColor: 'var(--info-red-bg)', 
                color: 'var(--info-red-text)',
                border: '1px solid var(--info-red-border)'
              }}
              title={t('buttons.delete')}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AudioCommentTile;
